import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RiShoppingBag3Line,
  RiMoneyDollarCircleLine,
  RiHistoryLine,
  RiDeleteBinLine,
  RiToolsLine,
} from 'react-icons/ri';
import productService from '../../services/productService';
import maintenanceTypeService, { MaintenanceType } from '../../services/maintenanceTypeService';
import { usePermission } from '../../hooks/usePermission';
import { queryKeys } from '../../queryClient';
import { PRODUCTS_PATH, productEditUrl } from '../../utils/productLinks';
import { parseRouteId } from '../../utils/routeId';
import { showAlert, showConfirm } from '../../utils/alert';
import { formatDateTimeFull } from '../../utils/dateUtils';
import { productFromApi, PRODUCT_FIELD_LABELS, formatBaht } from '../../components/product/productForm';
import {
  DetailPageLayout,
  DetailSection,
  InfoField,
  InfoGrid,
  RecordMetaCard,
  EntityActivityTimeline,
  RelatedRecordsCard,
  useDetailNavigation,
  headerButtonClass,
  headerButtonVariants,
} from '../../components/detail';
import { cn } from '../../utils/cn';

const LIST_PATH = PRODUCTS_PATH;

/**
 * หน้ารายละเอียดสินค้า — `/master-data/products/:id` (แทนโมดัล "รายละเอียดสินค้า" · มติ PO 28 ต.ค. 2026)
 * ข้อมูลครบทุกช่องของฟอร์มเดิม (อ่านอย่างเดียว) + ข้อมูล Flow Account + ข้อมูลการสร้าง/แก้ไข + ประวัติกิจกรรม
 *
 * การ์ดที่เกี่ยวข้อง: "ประเภทการซ่อมที่ผูกกับสินค้านี้" (maintenance_types.view) — API เดียวที่กรองด้วย product_id ได้จริง
 * (GET /maintenance-types/active?product_id= คืนประเภทที่ผูก + ประเภททั่วไป → กรองเหลือเฉพาะที่ผูกสินค้านี้)
 * สัญญาเช่ากรองได้แค่ product_name แบบ ilike (ชื่อคล้ายปนกัน) จึงไม่ทำการ์ดสัญญาเช่า
 */
const ProductViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const productId = parseRouteId(id);
  const { hasPermission } = usePermission();
  const queryClient = useQueryClient();
  const { backTo, goTo, navState } = useDetailNavigation(LIST_PATH);
  const [activityRefresh, setActivityRefresh] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const { data: product, isLoading, error, refetch } = useQuery({
    queryKey: [...queryKeys.products.all, 'detail', productId ?? 0],
    queryFn: async () => productFromApi(await productService.getProduct(productId!)),
    enabled: productId !== null,
  });

  const handleRefresh = () => {
    refetch();
    setActivityRefresh((n) => n + 1);
  };

  const handleDelete = () => {
    if (!product) return;
    showConfirm(
      'ยืนยันการลบสินค้า',
      `คุณต้องการลบสินค้า ${product.name} ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้ และอาจส่งผลกระทบต่อข้อมูลที่เกี่ยวข้อง`,
      async () => {
        setDeleting(true);
        try {
          const result = await productService.deleteProduct(product.id);
          if (result?.message) showAlert('สำเร็จ', result.message, 'success');
          queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
          goTo(backTo, { replace: true });
        } catch (e: any) {
          console.error('Delete error:', e);
          let errorMessage = 'เกิดข้อผิดพลาดในการลบสินค้า';
          if (e.response?.status === 404) {
            errorMessage = 'สินค้าที่ต้องการลบไม่มีอยู่ในระบบแล้ว';
          } else if (e.response?.data?.error?.user_message) {
            errorMessage = e.response.data.error.user_message;
          } else if (typeof e.response?.data?.detail === 'string') {
            errorMessage = e.response.data.detail;
          } else if (e.message) {
            errorMessage = e.message;
          }
          showAlert('เกิดข้อผิดพลาด', errorMessage, 'error');
        } finally {
          setDeleting(false);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  const notFound = productId === null ? { response: { status: 404 } } : null;
  const isActive = product?.isActive ?? true;

  return (
    <DetailPageLayout
      breadcrumbs={[{ label: 'สินค้า', path: backTo }, { label: product?.name || 'รายละเอียด' }]}
      icon={<RiShoppingBag3Line />}
      title={product?.name || 'รายละเอียดสินค้า'}
      recordId={product?.code}
      subtitle="รายละเอียดสินค้าและบริการ"
      backTo={backTo}
      onRefresh={handleRefresh}
      editTo={product ? productEditUrl(product.id) : undefined}
      editPermission="products.edit"
      editState={navState}
      actions={
        product && hasPermission('products.delete') ? (
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className={cn(headerButtonClass, headerButtonVariants.danger, 'disabled:opacity-50')}
          >
            <RiDeleteBinLine className="h-4 w-4 mr-1.5" />
            ลบ
          </button>
        ) : undefined
      }
      loading={productId !== null && isLoading}
      error={notFound || error}
      notFoundMessage="ไม่พบสินค้าที่ระบุ (อาจถูกลบไปแล้ว)"
      onRetry={() => refetch()}
      sidebar={
        product && (
          <>
            <RecordMetaCard
              createdAt={product.created_at}
              createdBy={product.created_by || product.createdBy}
              updatedAt={product.updated_at}
              updatedBy={product.updated_by || product.updatedBy}
              extra={
                product.syncedFromFlow
                  ? [
                      { label: 'Flow Account ID', value: product.flowaccountId || '-' },
                      { label: 'Sync ล่าสุดเมื่อ', value: formatDateTimeFull(product.flowaccountSyncDate ?? null) },
                    ]
                  : []
              }
            />
            <DetailSection title="ประวัติกิจกรรม" icon={<RiHistoryLine />}>
              <EntityActivityTimeline
                entityType="product"
                entityId={product.id}
                refreshTrigger={activityRefresh}
                fieldLabels={PRODUCT_FIELD_LABELS}
                valueFormatters={{
                  is_active: (v) => (v ? 'เปิดใช้งาน' : 'ปิดใช้งาน'),
                  monthly_rental_rate: (v) => formatBaht(v),
                  deposit_amount: (v) => formatBaht(v),
                  minimum_rental_days: (v) => `${v} วัน`,
                }}
              />
            </DetailSection>
          </>
        )
      }
    >
      {product && (
        <>
          <DetailSection
            title="ข้อมูลพื้นฐาน"
            icon={<RiShoppingBag3Line />}
            actions={
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border',
                  isActive ? 'bg-success-50 text-success-700 border-success-200' : 'bg-secondary-50 text-secondary-600 border-secondary-200'
                )}
                data-testid="product-status-badge"
              >
                <span className={cn('w-1.5 h-1.5 rounded-full', isActive ? 'bg-success-500' : 'bg-secondary-400')} />
                {isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
              </span>
            }
          >
            <InfoGrid>
              <InfoField label="รหัสสินค้า" value={product.code} mono />
              <InfoField label="ชื่อสินค้า" value={product.name} />
              <InfoField label="รายละเอียด" value={product.description} multiline fullWidth />
              <InfoField label="หน่วย" value={product.unit} />
              <InfoField label="Sync จาก Flow">
                <span
                  className={cn(
                    'px-2 py-0.5 inline-flex text-xs font-semibold rounded-full',
                    product.syncedFromFlow ? 'bg-info-100 text-info-800' : 'bg-secondary-100 text-secondary-800'
                  )}
                >
                  {product.syncedFromFlow ? 'Sync แล้ว' : 'ยังไม่ Sync'}
                </span>
              </InfoField>
            </InfoGrid>
          </DetailSection>

          <DetailSection title="ราคาและเงื่อนไขการเช่า" icon={<RiMoneyDollarCircleLine />}>
            <InfoGrid>
              <InfoField label="ค่าเช่ารายเดือน" value={formatBaht(product.monthlyRentalRate)} />
              <InfoField label="เงินมัดจำ" value={formatBaht(product.depositAmount)} />
              <InfoField label="จำนวนวันเช่าขั้นต่ำ" value={`${product.minimumRentalDays} วัน`} />
            </InfoGrid>
          </DetailSection>

          <RelatedRecordsCard<MaintenanceType>
            title="ประเภทการซ่อมที่ผูกกับสินค้านี้"
            icon={<RiToolsLine />}
            permission="maintenance_types.view"
            queryKey={['product-related', product.id, 'maintenance-types']}
            fetcher={async () => {
              // /active?product_id= คืนประเภทที่ผูกสินค้านี้ + ประเภท "ทั่วไป" (ไม่ผูกสินค้าใด) → เหลือเฉพาะที่ผูกจริง
              const all = await maintenanceTypeService.getActiveMaintenanceTypes(product.id);
              const linked = (all || []).filter((t) => (t.products || []).some((p) => p.id === product.id));
              return { items: linked, total: linked.length };
            }}
            rowKey={(t) => t.id}
            viewAllTo="/settings/maintenance-types"
            emptyText="ยังไม่มีประเภทการซ่อมที่ผูกกับสินค้านี้ (ใช้ประเภททั่วไป)"
            testId="product-related-maintenance-types"
            renderRow={(t) => (
              <>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-secondary-900 [overflow-wrap:anywhere]">{t.name}</p>
                  <p className="text-xs text-secondary-500 font-mono">{t.code}</p>
                </div>
                <span className="text-xs text-success-700 flex-shrink-0">เปิดใช้งาน</span>
              </>
            )}
          />
        </>
      )}
    </DetailPageLayout>
  );
};

export default ProductViewPage;
